import { useMemo, useState } from 'react';
import type { Customer, CustomerLocation } from '@/services/customer.service';
import type { AddressSelection } from '@/services/equipment-intake.service';
import { AddressPicker } from './AddressPicker';
import { AddressCard } from './AddressCard';
import { AddressFormModal, type AddressFormMode } from './AddressFormModal';
import { buildAddressOptions, COMPANY_OPTION_ID, optionIdFor } from './address-labels';
import { resolveAddressPreview } from './address-preview';
import type { AddressSide } from './address-side';

/**
 * Bill-To and Ship-To together, with the shared Add / Edit dialog.
 *
 * Composed here rather than in each page so the three screens that capture
 * addresses — the intake workspace, the intake create form, and the post-save
 * edit dialog — cannot drift apart in wording or behaviour.
 */
export function AddressSection({
  customerId,
  customer,
  locations,
  billTo,
  shipTo,
  onBillToChange,
  onShipToChange,
  disabled = false,
}: {
  customerId: string;
  customer: Customer | null;
  /** Active (non-deleted) locations for this customer. */
  locations: CustomerLocation[];
  billTo: AddressSelection;
  shipTo: AddressSelection;
  onBillToChange: (next: AddressSelection) => void;
  onShipToChange: (next: AddressSelection) => void;
  disabled?: boolean;
}) {
  const [modal, setModal] = useState<{ side: AddressSide; mode: AddressFormMode } | null>(null);

  const options = useMemo(() => buildAddressOptions(customer, locations), [customer, locations]);
  const locationsById = useMemo(() => new Map(locations.map((l) => [l.id, l])), [locations]);

  const billPreview = resolveAddressPreview('Bill-To', billTo, customer, locations);
  const shipPreview = resolveAddressPreview('Ship-To', shipTo, customer, locations);

  /** Whichever address the side currently points at is what Edit opens. */
  const editModeFor = (selection: AddressSelection): AddressFormMode | null => {
    const id = optionIdFor(selection);
    if (id === COMPANY_OPTION_ID) {
      return customer ? { kind: 'edit-company', customer } : null;
    }
    const location = locationsById.get(id);
    return location ? { kind: 'edit-place', location } : null;
  };

  const openEdit = (side: AddressSide, selection: AddressSelection) => {
    const mode = editModeFor(selection);
    if (mode) setModal({ side, mode });
  };

  const applySaved = (side: AddressSide, selection: AddressSelection) => {
    if (side === 'Bill-To') onBillToChange(selection);
    else onShipToChange(selection);
  };

  const sides: {
    side: AddressSide;
    value: AddressSelection;
    onChange: (n: AddressSelection) => void;
    preview: ReturnType<typeof resolveAddressPreview>;
  }[] = [
    { side: 'Bill-To', value: billTo, onChange: onBillToChange, preview: billPreview },
    { side: 'Ship-To', value: shipTo, onChange: onShipToChange, preview: shipPreview },
  ];

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {sides.map(({ side, value, onChange, preview }) => (
          <div key={side}>
            <AddressPicker
              side={side}
              value={value}
              onChange={onChange}
              options={options}
              locationsById={locationsById}
              onAddAddress={() => setModal({ side, mode: { kind: 'create' } })}
              disabled={disabled}
            />
            <AddressCard
              side={side}
              address={preview}
              canEdit={!disabled && preview !== null}
              onEdit={() => openEdit(side, value)}
            />
          </div>
        ))}
      </div>

      {modal && (
        <AddressFormModal
          open
          customerId={customerId}
          mode={modal.mode}
          onClose={() => setModal(null)}
          onSaved={(selection) => applySaved(modal.side, selection)}
        />
      )}
    </>
  );
}
